import React, { useState, useEffect } from 'react';
import {
  Card,
  Tabs,
  Form,
  Input,
  Button,
  Switch,
  Avatar,
  Upload,
  message,
  Divider,
  Row,
  Col,
  Select,
  Space,
  Typography,
  Badge,
  Modal,
  Alert,
} from 'antd';
import {
  UserOutlined,
  SettingOutlined,
  LockOutlined,
  BellOutlined,
  BgColorsOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  UploadOutlined,
  SaveOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { useTheme } from '../../hooks/useTheme';
import UserConfigService, { UserProfileData, UserPreferences } from '../../services/UserConfigService';
import styles from './UserConfig.module.css';

const { TabPane } = Tabs;
const { Title, Text } = Typography;
const { Option } = Select;

export interface UserConfigProps {
  visible: boolean;
  onClose: () => void;
  currentUser?: {
    id: number;
    name: string;
    email: string;
    rol: string;
  };
}

const UserConfig: React.FC<UserConfigProps> = ({ visible, onClose, currentUser }) => {
  const { theme, toggleTheme, setThemeMode, isDark } = useTheme();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');
  const [userProfile, setUserProfile] = useState<UserProfileData | null>(null);
  const [preferences, setPreferences] = useState<UserPreferences>(UserConfigService.getUserPreferences());
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [passwordForm] = Form.useForm();
  const [profileForm] = Form.useForm();
  const [photoLoading, setPhotoLoading] = useState(false);
  const [photoSrc, setPhotoSrc] = useState<string | null>(null);
  const [targetUserId, setTargetUserId] = useState<number | null>(null);

  useEffect(() => {
    if (visible && currentUser) {
      fetchUserProfile();
      loadPreferences();
      fetchUserPhoto(currentUser.id);
    }
  }, [visible, currentUser]);

  const fetchUserPhoto = async (userId: number) => {
    setPhotoLoading(true);
    try {
      const dataUrl = await UserConfigService.getUserPhotoDataUrl(userId);
      setPhotoSrc(dataUrl);
    } catch (error: any) {
      // ignore, show default avatar
    } finally {
      setPhotoLoading(false);
    }
  };

  const fetchUserProfile = async () => {
    if (!currentUser) return;
    
    setLoading(true);
    try {
      const profile = await UserConfigService.getUserProfile(currentUser.id);
      setUserProfile(profile);
      profileForm.setFieldsValue(profile);
    } catch (error: any) {
      message.error(`Error al cargar el perfil: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const loadPreferences = () => {
    const prefs = UserConfigService.getUserPreferences();
    setPreferences(prefs);
  };

  const handleProfileUpdate = async (values: any) => {
    if (!currentUser) return;

    setLoading(true);
    try {
      const updatedProfile = await UserConfigService.updateUserProfile(currentUser.id, values);
      setUserProfile(updatedProfile);
      message.success('Perfil actualizado exitosamente');
    } catch (error: any) {
      message.error(`Error al actualizar el perfil: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (values: any) => {
    if (!currentUser) return;

    setLoading(true);
    try {
      await UserConfigService.changePassword(
        currentUser.id,
        values.currentPassword,
        values.newPassword
      );
      message.success('Contraseña cambiada exitosamente');
      passwordForm.resetFields();
    } catch (error: any) {
      message.error(`Error al cambiar la contraseña: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleThemeChange = (newTheme: 'light' | 'dark') => {
    setThemeMode(newTheme);
    const updatedPrefs = { ...preferences, theme: newTheme };
    setPreferences(updatedPrefs);
    UserConfigService.setUserPreferences(updatedPrefs);
    message.success(`Tema cambiado a ${newTheme === 'dark' ? 'oscuro' : 'claro'}`);
  };

  const handlePreferenceChange = (key: string, value: any) => {
    const updatedPrefs = { ...preferences };
    const keys = key.split('.');
    let current: any = updatedPrefs;
    
    for (let i = 0; i < keys.length - 1; i++) {
      current = current[keys[i]];
    }
    
    current[keys[keys.length - 1]] = value;
    setPreferences(updatedPrefs);
    UserConfigService.setUserPreferences(updatedPrefs);
  };

  const resetPreferences = () => {
    Modal.confirm({
      title: '¿Restablecer configuración?',
      content: 'Esto restaurará todas las configuraciones a sus valores por defecto.',
      onOk: () => {
        UserConfigService.resetUserPreferences();
        const defaultPrefs = UserConfigService.getUserPreferences();
        setPreferences(defaultPrefs);
        setThemeMode(defaultPrefs.theme);
        message.success('Configuración restablecida');
      },
    });
  };

  return (
    <Modal
      title={
        <Space>
          <SettingOutlined />
          <span>Configuración de Usuario</span>
        </Space>
      }
      open={visible}
      onCancel={onClose}
      footer={null}
      width={800}
      className={styles.userConfigModal}
    >
      <Tabs activeKey={activeTab} onChange={setActiveTab} className={styles.configTabs}>
        {}
        <TabPane
          tab={
            <Space>
              <UserOutlined />
              <span>Perfil</span>
            </Space>
          }
          key="profile"
        >
          <div className={styles.tabContent}>
            <Row gutter={24}>
              <Col span={8}>
                <div className={styles.avatarSection}>
                  <Avatar
                    size={80}
                    icon={<UserOutlined />}
                    className={styles.userAvatar}
                  />
                  <Upload
                    showUploadList={false}
                    beforeUpload={() => {
                      message.info('Funcionalidad de avatar pendiente de implementación');
                      return false;
                    }}
                  >
                    <Button icon={<UploadOutlined />} size="small" className={styles.uploadBtn}>
                      Cambiar Avatar
                    </Button>
                  </Upload>
                </div>
              </Col>
              <Col span={16}>
                <Form
                  form={profileForm}
                  layout="vertical"
                  onFinish={handleProfileUpdate}
                  className={styles.profileForm}
                >
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        label="Nombre"
                        name="name"
                        rules={[{ required: true, message: 'El nombre es obligatorio' }]}
                      >
                        <Input placeholder="Ingrese su nombre completo" />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        label="Cédula"
                        name="cedula"
                        rules={[{ required: true, message: 'La cédula es obligatoria' }]}
                      >
                        <Input placeholder="Número de cédula" />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Form.Item
                    label="Email"
                    name="email"
                    rules={[
                      { required: true, message: 'El email es obligatorio' },
                      { type: 'email', message: 'Ingrese un email válido' }
                    ]}
                  >
                    <Input placeholder="correo@ejemplo.com" />
                  </Form.Item>
                  <Form.Item>
                    <Button
                      type="primary"
                      htmlType="submit"
                      loading={loading}
                      icon={<SaveOutlined />}
                    >
                      Guardar Cambios
                    </Button>
                  </Form.Item>
                </Form>
              </Col>
            </Row>
          </div>
        </TabPane>

        {}
        <TabPane
          tab={
            <Space>
              <UploadOutlined />
              <span>Foto</span>
            </Space>
          }
          key="photo"
        >
          <div className={styles.tabContent}>
            <Row gutter={24}>
              <Col span={8}>
                <div style={{ textAlign: 'center' }}>
                  <Avatar size={120} src={photoSrc || undefined} icon={!photoSrc ? <UserOutlined /> : undefined} />
                  <div style={{ marginTop: 12 }}>
                    <Text type="secondary">Previsualización</Text>
                  </div>
                </div>
              </Col>

              <Col span={16}>
                <Form layout="vertical">
                  {}
                  {(currentUser && (currentUser.id === 1)) && (
                    <Form.Item label="Actualizar foto de (user_id)">
                      <Input
                        placeholder="Id del usuario objetivo (ej: 4)"
                        type="number"
                        value={targetUserId ?? ''}
                        onChange={(e) => setTargetUserId(Number(e.target.value) || null)}
                      />
                    </Form.Item>
                  )}

                  <Form.Item label="Seleccionar archivo">
                    <Upload
                      accept="image/*"
                      showUploadList={false}
                      beforeUpload={(file) => {
                        const targetId = targetUserId ?? currentUser?.id;
                        if (!targetId) {
                          message.error('No hay usuario objetivo seleccionado');
                          return false;
                        }

                        setPhotoLoading(true);
                        UserConfigService.uploadUserPhoto(targetId, file as File)
                          .then(() => {
                            message.success('Foto actualizada correctamente');
                            // refresh preview if the updated photo belongs to the currently previewed user
                            if (targetId === (currentUser?.id ?? 0)) fetchUserPhoto(targetId);
                          })
                          .catch((err: any) => {
                            message.error(`Error al subir la foto: ${err.message || err}`);
                          })
                          .finally(() => setPhotoLoading(false));

                        return false; // prevent default upload
                      }}
                    >
                      <Button icon={<UploadOutlined />} loading={photoLoading}>
                        Seleccionar y subir
                      </Button>
                    </Upload>
                  </Form.Item>

                  <Form.Item>
                    <Text type="secondary">Nota: El endpoint acepta FormData con los campos <Text code>user_id</Text> y <Text code>photo</Text>.</Text>
                  </Form.Item>
                </Form>
              </Col>
            </Row>
          </div>
        </TabPane>

        {}
        <TabPane
          tab={
            <Space>
              <LockOutlined />
              <span>Seguridad</span>
            </Space>
          }
          key="security"
        >
          <div className={styles.tabContent}>
            <Title level={4}>Cambiar Contraseña</Title>
            <Form
              form={passwordForm}
              layout="vertical"
              onFinish={handlePasswordChange}
              className={styles.passwordForm}
            >
              <Form.Item
                label="Contraseña Actual"
                name="currentPassword"
                rules={[{ required: true, message: 'Ingrese su contraseña actual' }]}
              >
                <Input.Password
                  placeholder="Contraseña actual"
                  iconRender={(visible) => (visible ? <EyeOutlined /> : <EyeInvisibleOutlined />)}
                />
              </Form.Item>
              <Form.Item
                label="Nueva Contraseña"
                name="newPassword"
                rules={[
                  { required: true, message: 'Ingrese la nueva contraseña' },
                  { min: 6, message: 'La contraseña debe tener al menos 6 caracteres' }
                ]}
              >
                <Input.Password
                  placeholder="Nueva contraseña"
                  iconRender={(visible) => (visible ? <EyeOutlined /> : <EyeInvisibleOutlined />)}
                />
              </Form.Item>
              <Form.Item
                label="Confirmar Nueva Contraseña"
                name="confirmPassword"
                dependencies={['newPassword']}
                rules={[
                  { required: true, message: 'Confirme la nueva contraseña' },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('newPassword') === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error('Las contraseñas no coinciden'));
                    },
                  }),
                ]}
              >
                <Input.Password
                  placeholder="Confirmar nueva contraseña"
                  iconRender={(visible) => (visible ? <EyeOutlined /> : <EyeInvisibleOutlined />)}
                />
              </Form.Item>
              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  icon={<SaveOutlined />}
                >
                  Cambiar Contraseña
                </Button>
              </Form.Item>
            </Form>
          </div>
        </TabPane>

        {}
        <TabPane
          tab={
            <Space>
              <BgColorsOutlined />
              <span>Apariencia</span>
            </Space>
          }
          key="appearance"
        >
          <div className={styles.tabContent}>
            <Title level={4}>Tema</Title>
            <div className={styles.themeSelector}>
              <Row gutter={16}>
                <Col span={12}>
                  <Card
                    className={`${styles.themeCard} ${theme === 'light' ? styles.selected : ''}`}
                    onClick={() => handleThemeChange('light')}
                    hoverable
                  >
                    <div className={styles.lightThemePreview}>
                      <div className={styles.previewHeader}></div>
                      <div className={styles.previewContent}>
                        <div className={styles.previewSidebar}></div>
                        <div className={styles.previewMain}></div>
                      </div>
                    </div>
                    <Text strong>Tema Claro</Text>
                    {theme === 'light' && <Badge status="success" text="Activo" />}
                  </Card>
                </Col>
                <Col span={12}>
                  <Card
                    className={`${styles.themeCard} ${theme === 'dark' ? styles.selected : ''}`}
                    onClick={() => handleThemeChange('dark')}
                    hoverable
                  >
                    <div className={styles.darkThemePreview}>
                      <div className={styles.previewHeader}></div>
                      <div className={styles.previewContent}>
                        <div className={styles.previewSidebar}></div>
                        <div className={styles.previewMain}></div>
                      </div>
                    </div>
                    <Text strong>Tema Oscuro</Text>
                    {theme === 'dark' && <Badge status="success" text="Activo" />}
                  </Card>
                </Col>
              </Row>
            </div>

            <Divider />

            <Title level={4}>Preferencias de Dashboard</Title>
            <div className={styles.preferencesSection}>
              <Row gutter={16}>
                <Col span={12}>
                  <div className={styles.preferenceItem}>
                    <Text>Modo Compacto</Text>
                    <Switch
                      checked={preferences.dashboard.compactMode}
                      onChange={(checked) => handlePreferenceChange('dashboard.compactMode', checked)}
                    />
                  </div>
                </Col>
                <Col span={12}>
                  <div className={styles.preferenceItem}>
                    <Text>Mostrar Mensaje de Bienvenida</Text>
                    <Switch
                      checked={preferences.dashboard.showWelcome}
                      onChange={(checked) => handlePreferenceChange('dashboard.showWelcome', checked)}
                    />
                  </div>
                </Col>
              </Row>
            </div>
          </div>
        </TabPane>

        {}
        <TabPane
          tab={
            <Space>
              <BellOutlined />
              <span>Notificaciones</span>
            </Space>
          }
          key="notifications"
        >
          <div className={styles.tabContent}>
            <Title level={4}>Configuración de Notificaciones</Title>
            <div className={styles.notificationsSection}>
              <div className={styles.notificationItem}>
                <div>
                  <Text strong>Notificaciones por Email</Text>
                  <br />
                  <Text type="secondary">Recibir notificaciones importantes por correo electrónico</Text>
                </div>
                <Switch
                  checked={preferences.notifications.email}
                  onChange={(checked) => handlePreferenceChange('notifications.email', checked)}
                />
              </div>
              
              <div className={styles.notificationItem}>
                <div>
                  <Text strong>Notificaciones del Navegador</Text>
                  <br />
                  <Text type="secondary">Mostrar notificaciones en el navegador</Text>
                </div>
                <Switch
                  checked={preferences.notifications.browser}
                  onChange={(checked) => handlePreferenceChange('notifications.browser', checked)}
                />
              </div>
              
              <div className={styles.notificationItem}>
                <div>
                  <Text strong>Notificaciones de Escritorio</Text>
                  <br />
                  <Text type="secondary">Notificaciones del sistema operativo</Text>
                </div>
                <Switch
                  checked={preferences.notifications.desktop}
                  onChange={(checked) => handlePreferenceChange('notifications.desktop', checked)}
                />
              </div>
            </div>

            <Alert
              message="Información"
              description="Las notificaciones de escritorio requieren permisos del navegador. Algunas funciones pueden no estar disponibles según su configuración."
              type="info"
              showIcon
              style={{ marginTop: 20 }}
            />
          </div>
        </TabPane>
      </Tabs>

      <Divider />
      
      <div className={styles.modalFooter}>
        <Space>
          <Button onClick={resetPreferences} icon={<ReloadOutlined />}>
            Restablecer Configuración
          </Button>
          <Button onClick={onClose}>
            Cerrar
          </Button>
        </Space>
      </div>
    </Modal>
  );
};

export default UserConfig;
